/**
 * ECharts line chart renderer — best for time-series trends.
 *
 * Supports multiple metrics as separate lines, or a single metric
 * with series_by to compare entities over time. Optionally overlays
 * group comparison lines from other scopes.
 */
import ReactECharts from 'echarts-for-react'
import type { PerfRow, MetricKey, GroupByDimension } from './types'
import { METRIC_LABELS, formatMetric, getDimensionLabel } from './types'
import type { BenchmarkDataSeries } from './useBenchmarkQueries'
import { makeChartReady } from './echarts-theme'

interface Props {
  data: PerfRow[]
  groupBy: GroupByDimension
  metrics: MetricKey[]
  seriesBy?: GroupByDimension
  groupComparisons?: BenchmarkDataSeries[]
  primaryLabel?: string
  /**
   * Goal line overlay (Phase D2): a horizontal markLine at the goal target,
   * shown when a widget's measure is goal-tracked for the user's portfolio.
   */
  goalLine?: {
    value: number
    label: string
    direction?: 'higher_is_better' | 'lower_is_better'
  }
  /** ECharts group key for cross-chart hover/zoom sync (Phase F1). */
  chartGroup?: string
}

/** Whether a groupBy is a time axis (and thus should get a dataZoom slider). */
function isTimeAxis(groupBy: GroupByDimension): boolean {
  return groupBy === 'fiscal_year' || groupBy === 'fiscal_quarter'
}

const getLabel = getDimensionLabel

const COLORS = [
  '#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6',
  '#06b6d4', '#ec4899', '#14b8a6', '#f97316', '#6366f1',
]

/** Compute simple average of a metric across comparison rows. */
function computeAvg(rows: PerfRow[], metric: MetricKey): number | null {
  let sum = 0, count = 0
  for (const row of rows) {
    const val = row[metric]
    if (typeof val === 'number' && !isNaN(val)) { sum += val; count++ }
  }
  return count > 0 ? sum / count : null
}

/**
 * Append comparison data to a series array. If comparison data shares the
 * same dimension as the primary categories, overlay as dashed lines.
 * Otherwise, show as horizontal markLines at the average value.
 */
function appendComparisonSeries(
  series: any[],
  comparisons: BenchmarkDataSeries[],
  categories: string[],
  groupBy: GroupByDimension,
  metric: MetricKey,
) {
  for (const comp of comparisons) {
    if (comp.data.length === 0) continue

    // Check if comparison data matches the primary dimension
    const compLabels = comp.data.map((r) => getLabel(r, groupBy))
    const matchCount = compLabels.filter((l) => categories.includes(l)).length

    if (matchCount > 0) {
      // Dimensions match — overlay as dashed line
      const compMap = new Map<string, number>()
      for (const row of comp.data) {
        const label = getLabel(row, groupBy)
        const val = row[metric]
        compMap.set(label, typeof val === 'number' ? val : parseFloat(String(val)) || 0)
      }
      series.push({
        name: comp.source.label,
        type: 'line' as const,
        data: categories.map((cat) => compMap.get(cat) ?? null),
        smooth: true,
        symbol: 'diamond',
        symbolSize: 4,
        lineStyle: { width: 1.5, type: 'dashed', color: comp.source.color || '#8b5cf6' },
        itemStyle: { color: comp.source.color || '#8b5cf6' },
      })
    } else {
      // Different dimension — show as horizontal reference line
      const avg = computeAvg(comp.data, metric)
      if (avg !== null) {
        if (!series[0].markLine) {
          series[0].markLine = { data: [], symbol: 'none', silent: true }
        }
        series[0].markLine.data.push({
          yAxis: avg,
          lineStyle: { type: 'dashed', color: comp.source.color || '#8b5cf6', width: 1.5 },
          label: {
            formatter: `${comp.source.label}: ${formatMetric(metric, avg)}`,
            fontSize: 10,
            color: comp.source.color || '#8b5cf6',
            position: 'insideEndTop',
          },
        })
      }
    }
  }
}

export default function LineChartRenderer({ data, groupBy, metrics, seriesBy, groupComparisons, primaryLabel, goalLine, chartGroup }: Props) {
  // F2: dataZoom on time-axis line charts so multi-FY views are navigable
  // without a re-query. Skipped for non-time-axis lines (rare).
  const dataZoom = isTimeAxis(groupBy)
    ? [{ type: 'inside' as const }, { type: 'slider' as const, height: 16, bottom: 6 }]
    : undefined
  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-gray-400">
        No data
      </div>
    )
  }

  const hasComparisons = groupComparisons && groupComparisons.some((c) => c.data.length > 0)
  const metric = metrics[0]

  /** Append goal-line markLine to the first series. Phase D2. */
  function applyGoalLine(series: any[]) {
    if (!goalLine || series.length === 0) return
    if (!series[0].markLine) {
      series[0].markLine = { data: [], symbol: 'none', silent: true }
    }
    series[0].markLine.data.push({
      yAxis: goalLine.value,
      lineStyle: { type: 'solid', color: '#f59e0b', width: 2 },
      label: {
        formatter: goalLine.label,
        fontSize: 10,
        color: '#f59e0b',
        position: 'insideEndTop',
      },
    })
  }

  // If seriesBy, pivot into multiple lines
  if (seriesBy) {
    const groupLabels: string[] = []
    const seriesLabels = new Set<string>()
    const pivoted = new Map<string, Map<string, number>>()

    for (const row of data) {
      const gLabel = getLabel(row, groupBy)
      const sLabel = getLabel(row, seriesBy)
      seriesLabels.add(sLabel)
      if (!pivoted.has(gLabel)) {
        pivoted.set(gLabel, new Map())
        groupLabels.push(gLabel)
      }
      const val = typeof row[metrics[0]] === 'number' ? (row[metrics[0]] as number) : 0
      pivoted.get(gLabel)!.set(sLabel, val)
    }

    const seriesArr = Array.from(seriesLabels)
    const series: any[] = seriesArr.map((sLabel, i) => ({
      name: sLabel,
      type: 'line' as const,
      data: groupLabels.map((gl) => pivoted.get(gl)?.get(sLabel) ?? null),
      smooth: true,
      symbol: 'circle',
      symbolSize: 6,
      lineStyle: { width: 2 },
      itemStyle: { color: COLORS[i % COLORS.length] },
    }))

    // Append comparison lines as dashed overlays
    if (hasComparisons) {
      appendComparisonSeries(series, groupComparisons!, groupLabels, groupBy, metric)
    }

    // Goal-line overlay (D2)
    applyGoalLine(series)

    return (
      <ReactECharts
        option={{
          tooltip: { trigger: 'axis' },
          legend: { top: 0, textStyle: { fontSize: 11 } },
          grid: { left: 50, right: 20, top: 40, bottom: dataZoom ? 36 : 30 },
          xAxis: { type: 'category', data: groupLabels, axisLabel: { fontSize: 10 } },
          yAxis: { type: 'value', axisLabel: { fontSize: 10 } },
          ...(dataZoom ? { dataZoom } : {}),
          series,
        }}
        style={{ height: '100%', width: '100%' }}
        opts={{ renderer: 'canvas' }}
        onChartReady={makeChartReady(chartGroup)}
      />
    )
  }

  // Multi-metric lines (or single metric + comparisons)
  const categories = data.map((r) => getLabel(r, groupBy))
  const series: any[] = metrics.map((m, i) => ({
    name: hasComparisons ? (primaryLabel || 'Primary') : METRIC_LABELS[m],
    type: 'line' as const,
    data: data.map((r) => {
      const val = r[m]
      return typeof val === 'number' ? val : parseFloat(String(val)) || 0
    }),
    smooth: true,
    symbol: 'circle',
    symbolSize: 6,
    lineStyle: { width: 2 },
    itemStyle: { color: COLORS[i % COLORS.length] },
  }))

  // Append comparison lines
  if (hasComparisons) {
    appendComparisonSeries(series, groupComparisons!, categories, groupBy, metric)
  }

  // Goal-line overlay (D2)
  applyGoalLine(series)

  const showLegend = hasComparisons || metrics.length > 1

  const option = {
    tooltip: {
      trigger: 'axis' as const,
      formatter: (params: any) => {
        const items = Array.isArray(params) ? params : [params]
        const header = items[0]?.axisValue || ''
        const lines = items
          .filter((p: any) => p.value !== null && p.value !== undefined)
          .map((p: any) => `${p.marker} ${p.seriesName}: <b>${formatMetric(metric, p.value)}</b>`)
        return `<b>${header}</b><br/>${lines.join('<br/>')}`
      },
    },
    ...(showLegend ? { legend: { top: 0, textStyle: { fontSize: 11 } } } : {}),
    grid: { left: 50, right: 20, top: showLegend ? 40 : 15, bottom: dataZoom ? 36 : 30 },
    xAxis: { type: 'category' as const, data: categories, axisLabel: { fontSize: 10 } },
    yAxis: { type: 'value' as const, axisLabel: { fontSize: 10 } },
    ...(dataZoom ? { dataZoom } : {}),
    series,
  }

  return (
    <ReactECharts
      option={option}
      style={{ height: '100%', width: '100%' }}
      opts={{ renderer: 'canvas' }}
      onChartReady={makeChartReady(chartGroup)}
    />
  )
}
